import { useRef, useState, useEffect } from "react";
import { createRoot } from "react-dom/client";

(window as any).devData = {};

let updateSubscribers: (() => void)[] = [];

export const setDevCornerValue = (key: string, value: any) => {
  (window as any).devData[key] = value;
  updateSubscribers.forEach((callback) => callback());
};

const originalFetch = window.fetch;

let lastApiCallTime: number | null = null;

const logApiCall = (callData: any) => {
  const currentTime = Date.now();
  const existingCalls = (window as any).devData.apiCalls || [];
  const isNewBatch = lastApiCallTime && currentTime - lastApiCallTime > 500;
  lastApiCallTime = currentTime;

  setDevCornerValue(
    "apiCalls",
    isNewBatch ? [callData] : [...existingCalls, callData],
  );
};

window.fetch = async (url: string, config) => {
  try {
    const response = await originalFetch(url, config);
    const clone = response.clone();

    logApiCall({
      url,
      method: config?.method || "GET",
      requestData: config?.body,
      status: response.status,
      serverData: response.headers.get("X-Dev-Corner"),
      responseData: await clone.clone().text(),
    });

    return clone;
  } catch (error) {
    throw error;
  }
};

const ApiCallItem = ({ call, onClick }) => (
  <div style={styles.apiCall} onClick={onClick}>
    <div className="d-flex align-items-center">
      <span style={call.status === 200 ? styles.status : styles.errorStatus}>
        {call.status}
      </span>
      <span style={call.error ? styles.errorMethod : styles.method}>
        {call.method}
      </span>
      <span
        className="ml-2"
        style={{
          display: "inline-block",
          width: 475,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {call.url}
      </span>
    </div>
  </div>
);

const CopyToClipboard = ({ text, className = "" }) => {
  const [showSuccess, setShowSuccess] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
      }, 1500);
    });
  };

  return (
    <span
      onClick={handleCopy}
      style={{ cursor: "pointer", color: "#888" }}
      className={className}
      title="Copy to clipboard"
    >
      {showSuccess ? "✓ Copied!" : <i className="bi bi-copy" />}
    </span>
  );
};

export const DevCorner = () => {
  const devData = (window as any).devData;
  const [state, setState] = useState(() => {
    // On first visit, default to 'hidden' if nothing is set in localStorage
    const mode =
      (localStorage.getItem("devCornerMode") as "expanded" | "hidden") ||
      "hidden";
    return {
      selectedCall: null,
      mode,
      showCopySuccess: false,
    };
  });
  const { selectedCall, mode, showCopySuccess } = state;
  const cornerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const callback = () => setState((prev) => ({ ...prev }));
    updateSubscribers.push(callback);

    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.key.toLowerCase() === "d") {
        setState((prev) => ({
          ...prev,
          selectedCall: null,
          mode: prev.selectedCall
            ? prev.mode
            : prev.mode === "hidden"
              ? "expanded"
              : "hidden",
        }));
      }
    };

    const handleClickOutside = (event: MouseEvent) => {
      if (
        cornerRef.current &&
        !cornerRef.current.contains(event.target as Node)
      ) {
        setState((prev) => ({ ...prev, selectedCall: null }));
      }
    };

    window.addEventListener("keydown", handleKeyPress);
    window.addEventListener("mousedown", handleClickOutside);

    return () => {
      updateSubscribers = updateSubscribers.filter((cb) => cb !== callback);
      window.removeEventListener("keydown", handleKeyPress);
      window.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    document.body.classList.toggle("debug-highlight", mode !== "hidden");
    return () => {
      document.body.classList.remove("debug-highlight");
    };
  }, [mode]);

  useEffect(() => {
    localStorage.setItem("devCornerMode", mode);
  }, [mode]);

  const handleCopyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setState((prev) => ({ ...prev, showCopySuccess: true }));
      setTimeout(() => {
        setState((prev) => ({ ...prev, showCopySuccess: false }));
      }, 3000);
    });
  };

  if (mode === "hidden") return null;

  if (!selectedCall) {
    return (
      <div style={styles.container}>
        <div>
          <span>API Calls</span>
          <div style={styles.closeButton}>
            <span
              onClick={() => setState((prev) => ({ ...prev, mode: "hidden" }))}
            >
              ✕
            </span>
          </div>
        </div>
        {devData.apiCalls?.map((call) => (
          <ApiCallItem
            key={`${call.url}-${call.method}`}
            call={call}
            onClick={() =>
              setState((prev) => ({ ...prev, selectedCall: call }))
            }
          />
        ))}
      </div>
    );
  }

  return (
    <div
      ref={cornerRef}
      style={{ ...styles.container, maxHeight: "100vh", height: "100vh" }}
    >
      <div>
        <ApiCallItem call={selectedCall} onClick={() => {}} />
        <div style={styles.closeButton}>
          <span
            onClick={() =>
              setState((prev) => ({ ...prev, selectedCall: null }))
            }
          >
            ✕
          </span>
        </div>
      </div>

      <div className="mt-4">
        <div className="fs-12 gray fw-bold mb-1">Path</div>
        <div>{selectedCall.url?.split("?")[0]}</div>
      </div>

      <div className="mt-4">
        <div className="fs-12 gray fw-bold mb-1">Request Params</div>
        <div>{formatRequestParams(selectedCall)}</div>
      </div>

      {selectedCall.serverData &&
        (() => {
          const serverData = safeParseJson(selectedCall.serverData);
          if (typeof serverData === "object") {
            const filePath = `${serverData.file.replace(/^.*?(?=\/app\/)/, "")}:${
              serverData.line_number
            }`;
            return (
              <div className="mt-4">
                <div className="fs-12 gray fw-bold mb-1">API Endpoint</div>
                <div>
                  {serverData.controller}#{serverData.action}
                </div>
                <div
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  <a
                    href={`${serverData.editor}://file/${serverData.file}:${serverData.line_number}`}
                  >
                    {filePath}
                  </a>
                  <CopyToClipboard text={filePath.replace(/^\//, "")} />
                </div>
              </div>
            );
          }
        })()}

      <div className="mt-4">
        <div className="fs-12 gray fw-bold mb-1">Response</div>
        <div>{formatResponse(selectedCall.responseData)}</div>
      </div>
    </div>
  );
};

const formatRequestParams = (apiCall: any) => {
  const requestData = safeParseJson(apiCall.requestData);
  if (typeof requestData === "object") {
    return <pre>{JSON.stringify(requestData, null, 2)}</pre>;
  }
  const [, queryString] = apiCall.url.split("?");
  if (queryString) {
    const params = Object.fromEntries(new URLSearchParams(queryString));
    return <pre>{JSON.stringify(params, null, 2)}</pre>;
  }
  return <span className="gray">--</span>;
};

const formatResponse = (data: any) => {
  const body = safeParseJson(data);
  if (typeof body === "object") {
    return <pre>{JSON.stringify(body, null, 2)}</pre>;
  }
  return parseRailsError(data);
};

const safeParseJson = (data: any) => {
  try {
    return typeof data === "string" && data.trim().startsWith("{")
      ? JSON.parse(data)
      : data;
  } catch (e) {
    return data;
  }
};

const parseRailsError = (body: any) => {
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(body, "text/html");

    const title = doc.querySelector("h1")?.textContent || "";
    const message = doc.querySelector(".exception-message")?.textContent || "";
    const frames = Array.from(
      doc.querySelectorAll("#Application-Trace-0 .trace-frames"),
    ).map((frame) => frame.textContent);

    return (
      <div>
        <div className="fs-12 fw-bold">{title}</div>
        <div className="red">{message}</div>
        <div>
          {frames.map((frame, i) => (
            <div key={i} className="fs-10 monospace">
              {frame}
              <CopyToClipboard text={frame.trim().split(":in")[0]} />
            </div>
          ))}
        </div>
      </div>
    );
  } catch (e) {
    return body;
  }
};

const styles = {
  container: {
    position: "fixed",
    bottom: 0,
    left: 0,
    zIndex: 1000,
    backgroundColor: "#212121",
    color: "#e0e0e0",
    padding: "10px 15px",
    overflow: "auto",
    fontSize: "12px",
    minWidth: 600,
    width: "auto",
  },
  closeButton: {
    position: "absolute",
    top: "5px",
    right: "10px",
    cursor: "pointer",
    color: "#888",
    fontSize: "16px",
  },
  apiCall: {
    padding: "8px 0",
    cursor: "pointer",
  },
  method: {
    color: "#ff9800",
  },
  methodUrl: {
    marginLeft: "8px",
  },
  errorMethod: {
    color: "firebrick",
    fontWeight: "bold",
  },
  errorStatus: {
    backgroundColor: "firebrick",
    color: "white",
    padding: "2px 4px",
    borderRadius: "4px",
    marginRight: "8px",
    fontSize: "10px",
  },
  status: {
    backgroundColor: "green",
    color: "#e0e0e0",
    padding: "2px 4px",
    borderRadius: "4px",
    marginRight: "8px",
    fontSize: "10px",
  },
} as const;

export const initDevCorner = () => {
  const devCorner = document.createElement("div");
  devCorner.id = "dev-corner";
  document.body.appendChild(devCorner);
  createRoot(devCorner).render(<DevCorner />);
};

const getDebugFilePath = (Component) => {
  let fileName = "unknown";
  if (Component._source) {
    fileName = Component._source.fileName;
  } else {
    const match = Component.toString().match(/fileName:\s*"(\/[^"]+\.tsx?)"/g);
    fileName = match ? match[0].split('"')[1] : "unknown";
  }
  fileName = fileName.replace(window.env.root_path, "");
  return fileName;
};

export const wrapDebug = (children) => {
  return <div data-debug-filepath={getDebugFilePath(children)}>{children}</div>;
};

export const wrapComponent = (Component) => () => (
  <div data-debug-filepath={getDebugFilePath(Component)}>
    <Component />
  </div>
);

export const annotateDebugComponents = (routes) => {
  if ((window as any).env.environment !== "development") return routes;

  return routes.map((route) => {
    if (
      Array.isArray(route) &&
      route.length > 0 &&
      Array.isArray(route[route.length - 1]) &&
      route.length >= 3
    ) {
      const nested = annotateDebugComponents(route[route.length - 1]);
      const wrapped = wrapComponent(route[1]);
      return [route[0], wrapped, ...route.slice(2, route.length - 1), nested];
    } else if (Array.isArray(route)) {
      if (typeof route[1] === "function") {
        const wrapped = wrapComponent(route[1]);
        return [...route.slice(0, 1), wrapped, ...route.slice(2)];
      }
      return route;
    }
    return route;
  });
};
